import { MutationFunction, useMutation, useQueryClient } from 'react-query';
import { QueryKeySet } from './createQueryKeySet';

interface Params<TData, TVariables> {
  mutationFunc: MutationFunction<TData, TVariables>;
  keySet: QueryKeySet;
  keyExtractor(vars: TVariables): string | undefined;
  updater({ variables, item }: { variables: TVariables; item: TData }): TData;
}

export const createOptimisticUpdateMutationHook =
  <TData extends { id?: string }, TVariables>({
    mutationFunc,
    keyExtractor,
    updater,
    keySet,
  }: Params<TData, TVariables>) =>
  () => {
    const queryClient = useQueryClient();

    return useMutation(mutationFunc, {
      onMutate(vars) {
        const id = keyExtractor(vars);

        if (!id) return;

        queryClient.setQueriesData<TData[]>(keySet.lists(), (data = []) => {
          return data.map((item) =>
            item.id === id ? updater({ variables: vars, item }) : item
          );
        });

        queryClient.setQueryData<TData | undefined>(
          keySet.detail(id),
          (item) => {
            return item && updater({ variables: vars, item });
          }
        );
      },
    });
  };
